import React, { useEffect, useState } from 'react'
import { CircularProgressbar } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { useSelector } from 'react-redux'

import { BsCheckLg } from 'react-icons/bs'

import { nanoid } from 'nanoid'
import { useTranslation } from 'react-i18next'
import { isEmptyObject, calculateAverage } from '../../../../utils/utils'
import * as s from './ProfileScore.styles'
import useWindowDimensions from '../../../../hooks/useWindowDimensions'

export const styles = {
  // Customize the path, i.e. the "completed progress"
  path: {
    // Path color
    stroke: `#42DEB6`,
    // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
    strokeLinecap: 'butt',
    // Customize transition animation
    transition: 'stroke-dashoffset 0.5s ease 0s',
    // Rotate the path
    transform: 'rotate(0.25turn)',
    transformOrigin: 'center center',
  },
  // Customize the circle behind the path, i.e. the "total progress"
  trail: {
    // Trail color
    stroke: '#0000000d',
    // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
    strokeLinecap: 'butt',
    // Rotate the trail
    transform: 'rotate(0.25turn)',
    transformOrigin: 'center center',
  },
  // Customize the text
  text: {
    // Text color
    fill: '#000',
    // Text size
    fontSize: '14px',
    fontWeight: '700',
  },
  // Customize background - only used when the `background` prop is true
  background: {
    fill: '#3e98c7',
  },
}

export const stepsConfig = (t) => [
  {
    label: t('common:general'),
    subList: [
      { label: t('common:resume') },
      { label: t('common:table.contractType') },
      { label: t('common:candidates.searchStatus.placeholder') },
      { label: t('common:location') },
      { label: t('common:stepType.salaryfield') },
      { label: 'Linkedin' },
    ],
  },
  {
    label: t('common:table.jobdomain'),
    subList: [
      { label: t('common:table.jobdomain') },
      { label: t('common:table.subjobs') },
      { label: t('common:table.schoolLevel') },
    ],
  },
  {
    label: `${t('common:table.sectors')} - ${t('common:experienceForm.label')}`,
    subList: [
      { label: t('common:table.sectors') },
      { label: t('common:table.subsectors') },
      { label: t('common:experienceForm.label') },
    ],
  },
]

const ProfileScore = () => {
  const { t } = useTranslation(['common', 'talentArea'], { useSuspense: false })
  const talent = useSelector((state) => state.talent)
  const [score, setScore] = useState(0)
  const [steps, setSteps] = useState(stepsConfig(t))
  const { width } = useWindowDimensions()

  useEffect(() => {
    // General step
    const hasResume = !isEmptyObject(talent.resume)
    const hasLinkedin = !!talent.linkedinUrl.length

    // Experience step
    const hasJobDomain = !!talent.jobDomain
    const hasExperience = !!talent.experience.length

    // Contract step
    const hasContractTypes = !!talent.contractTypes.length
    const hasResearchStatus = !!talent.researchStatus.length
    const hasLocation = !!talent.location?.city
    const hasSalaryRange = !isEmptyObject(talent.salaryRange)

    const binaryValues = [
      +hasLinkedin,
      +hasResearchStatus,
      +hasLocation,
      +hasContractTypes,
      +hasExperience,
      +hasResume,
      +hasJobDomain,
      +hasSalaryRange,
    ]

    setScore(parseInt(calculateAverage(binaryValues) * 100, 10))
  }, [talent])

  if (width <= 767) {
    return null
  }
  return (
    <s.ProfileScoreWrapper>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
          alignItems: 'center',
        }}
      >
        <h3 style={{ alignSelf: 'flex-start', fontSize: '0.95rem' }}>
          {t(
            'talentArea:profileScore.label.complete',
            'Votre profil est complet à :',
          )}
        </h3>
        <div
          style={{
            width: 105,
            height: 105,
          }}
        >
          <CircularProgressbar
            value={score}
            text={`${score}%`}
            strokeWidth={18}
            styles={styles}
          />
        </div>
      </div>
      <div className="fill-infos-group">
        <ol style={{ paddingLeft: 15 }}>
          {stepsConfig(t).map((step) => (
            <li key={nanoid()} className="step-group">
              <span className="step-title">{step.label}</span>
              <ul className="step-sublist">
                {step.subList.map((element) => (
                  <ElementList key={nanoid()} {...element} />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </div>
    </s.ProfileScoreWrapper>
  )
}

export default ProfileScore

const ElementList = (props) => {
  const talent = useSelector((state) => state.talent)

  let condition
  switch (props.label) {
    case 'CV':
      condition = !isEmptyObject(talent.resume)
      break
    case 'Type de contrat':
      condition = talent?.contractTypes?.length > 0
      break
    case 'Disponibilités':
      condition =
        talent?.researchStatus?.length > 0 &&
        talent.researchStatus !== 'unknown'
      break
    case 'Lieu':
      condition = talent?.location?.formattedAddress?.length > 0
      break
    case 'Rémunération':
      condition = !isEmptyObject(talent.salaryRange)
      break
    case 'Linkedin':
      condition = talent?.linkedinUrl.length > 0
      break
    case 'Métier':
      condition = talent?.jobDomain.length > 0
      break
    case 'Sous métier':
      condition = talent?.subJob.length > 0
      break
    case 'Secteur':
      condition = talent?.sector.length > 0
      break
    case 'Sous-secteur':
      condition = talent?.subSector.length > 0
      break
    case "Niveau d'études":
      condition = talent?.schoolLevel.length > 0
      break
    case 'Expérience':
      condition = talent?.experience.length > 0
      break
    default:
      condition = !!talent[props.label]?.length
      break
  }

  return (
    <li key={props.label} style={{ margin: '5px 0' }}>
      <span
        style={{
          fontWeight: '700',
          fontSize: '13px',
          margin: '5px 0',
          color: condition ? 'black' : 'grey',
        }}
      >
        {props.label}
      </span>
      {condition && (
        <BsCheckLg color="#02cd9a" size={10} style={{ marginLeft: '5px' }} />
      )}
    </li>
  )
}
